import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import type { ReactNode } from 'react';

import { classNames } from '../Utils/classNames';
import { TextStyle } from './TextStyle';

export const BannerVariations = [
  'error',
  'warn',
  'success',
  'critical',
  'info',
] as const;

export type BannerProps = {
  status: (typeof BannerVariations)[number];
  title?: string;
  children: ReactNode;
  className?: string;
};

export function Banner({ status, title, children, className }: BannerProps) {
  const isError = status === 'error';
  const isWarning = status === 'warn';
  const isInfo = status === 'info';

  return (
    <section
      className={classNames(
        'flex space-x-2 rounded-sm p-2',
        {
          'bg-error-2': isError,
          'bg-primary-2': isInfo,
        },
        className
      )}
    >
      {isError && (
        <ExclamationTriangleIcon
          data-testid="bannerTextError"
          className="mt-1 h-4 w-4 shrink-0 text-xs text-error-6"
        />
      )}

      {isWarning && (
        <ExclamationCircleIcon
          data-testid="bannerTextWarning"
          className="mt-1 h-4 w-4 shrink-0 text-warning-6"
        />
      )}

      {isInfo && (
        <InformationCircleIcon
          data-testid="bannerTextInfo"
          className="h-5 w-5 shrink-0 text-primary-6"
        />
      )}

      {title ? (
        <div>
          <TextStyle variant="base-medium">{title}</TextStyle>
          <p data-testid="bannerText" className="text-sm">
            {children}
          </p>
        </div>
      ) : (
        <p data-testid="bannerText" className="text-sm">
          {children}
        </p>
      )}
    </section>
  );
}
